import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../config/firebase";
import "../../styles/admin/ClubAnalytics.css";

interface AnalyticsData {
  totalUsers: number;
  totalIssued: number;
  totalRedeemed: number;
  totalUnredeemed: number;
  redemptionRate: number;
  activeUsers: number;
  topProducts: Array<{ name: string; count: number }>;
  recentTransactions: Array<{
    id: string;
    type: string;
    amount: number;
    status: string;
    timestamp: Date;
    userName: string;
  }>;
}

interface UserBehavior {
  userId: string;
  displayName: string;
  email: string;
  totalSpent: number;
  transactionCount: number;
  lastActivity: Date;
  joinDate: Date;
  averageTransaction: number;
}

interface ProductPerformance {
  name: string;
  issued: number;
  redeemed: number;
  redemptionRate: number;
  totalValue: number;
}

export function ClubAnalyticsPage() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "custom">("30d");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");
  const [userBehaviors, setUserBehaviors] = useState<UserBehavior[]>([]);
  const [productPerformance, setProductPerformance] = useState<ProductPerformance[]>([]);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportEmail, setReportEmail] = useState<string>("");
  const [reportSchedule, setReportSchedule] = useState<"daily" | "weekly" | "monthly">("weekly");

  const db = getFirestore();

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, customStart, customEnd]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const now = new Date();
      let startDate: Date;
      
      if (timeRange === "custom") {
        if (!customStart || !customEnd) {
          setError("Please select both start and end dates");
          setLoading(false);
          return;
        }
        startDate = new Date(customStart);
        now.setHours(23, 59, 59, 999);
      } else {
        const daysAgo = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
        startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
      }

      const walletItemsSnapshot = await getDocs(collection(db, "wallet_items"));
      const walletItems = walletItemsSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const ledgerSnapshot = await getDocs(collection(db, "ledger"));
      const ledger = ledgerSnapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          createdAt: (doc.data() as any).createdAt?.toDate?.() || new Date(),
        }))
        .filter((tx) => (tx.createdAt as Date) >= startDate && (tx.createdAt as Date) <= now);

      const usersSnapshot = await getDocs(collection(db, "users"));
      const users = usersSnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        createdAt: (doc.data() as any).createdAt?.toDate?.() || new Date(),
      }));

      // Calculate metrics
      const totalIssued = walletItems.reduce((sum, item) => sum + ((item as any).value || 0), 0);
      const totalRedeemed = ledger
        .filter((tx) => (tx as any).type === "REDEEM")
        .reduce((sum, tx) => sum + ((tx as any).amount || 0), 0);
      const totalUnredeemed = totalIssued - totalRedeemed;
      const redemptionRate = totalIssued > 0 ? ((totalRedeemed / totalIssued) * 100).toFixed(1) : "0";

      const activeUserIds = new Set<string>();
      walletItems.forEach((item) => activeUserIds.add((item as any).userId));
      ledger.forEach((tx) => activeUserIds.add((tx as any).userId));

      const productCounts: Record<string, number> = {};
      walletItems.forEach((item) => {
        const productId = (item as any).productId;
        productCounts[productId] = (productCounts[productId] || 0) + 1;
      });

      const topProducts = Object.entries(productCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const recentTransactions = ledger
        .sort((a, b) => (b.createdAt as Date).getTime() - (a.createdAt as Date).getTime())
        .slice(0, 10)
        .map((tx) => {
          const user = users.find((u) => u.id === (tx as any).userId) as any;
          return {
            id: tx.id,
            type: (tx as any).type || "UNKNOWN",
            amount: (tx as any).amount || 0,
            status: (tx as any).status || "COMPLETED",
            timestamp: tx.createdAt as Date,
            userName: user?.displayName || user?.email || "Unknown",
          };
        });

      setAnalytics({
        totalUsers: users.length,
        totalIssued,
        totalRedeemed,
        totalUnredeemed,
        redemptionRate: parseFloat(redemptionRate as string),
        activeUsers: activeUserIds.size,
        topProducts,
        recentTransactions,
      });

      // User behavior analytics
      const behaviorMap: Record<string, UserBehavior> = {};
      users.forEach((user) => {
        behaviorMap[(user as any).id] = {
          userId: (user as any).id,
          displayName: (user as any).displayName || "Unknown",
          email: (user as any).email || "",
          totalSpent: 0,
          transactionCount: 0,
          lastActivity: new Date(0),
          joinDate: (user as any).createdAt || new Date(),
          averageTransaction: 0,
        };
      });

      ledger.forEach((tx) => {
        const userId = (tx as any).userId;
        if (behaviorMap[userId]) {
          behaviorMap[userId].totalSpent += (tx as any).amount || 0;
          behaviorMap[userId].transactionCount += 1;
          const txDate = tx.createdAt as Date;
          if (txDate > behaviorMap[userId].lastActivity) {
            behaviorMap[userId].lastActivity = txDate;
          }
        }
      });

      Object.values(behaviorMap).forEach((behavior) => {
        behavior.averageTransaction =
          behavior.transactionCount > 0 ? behavior.totalSpent / behavior.transactionCount : 0;
      });

      const sortedBehaviors = Object.values(behaviorMap)
        .filter((b) => b.transactionCount > 0)
        .sort((a, b) => b.totalSpent - a.totalSpent);
      setUserBehaviors(sortedBehaviors);

      // Product performance
      const performanceMap: Record<
        string,
        { issued: number; redeemed: number; totalValue: number }
      > = {};

      walletItems.forEach((item) => {
        const productId = (item as any).productId;
        if (!performanceMap[productId]) {
          performanceMap[productId] = { issued: 1, redeemed: 0, totalValue: (item as any).value };
        } else {
          performanceMap[productId].issued += 1;
          performanceMap[productId].totalValue += (item as any).value;
        }
      });

      ledger
        .filter((tx) => (tx as any).type === "REDEEM")
        .forEach((tx) => {
          const userId = (tx as any).userId;
          const userItem = walletItems.find((item) => (item as any).userId === userId);
          if (userItem) {
            const productId = (userItem as any).productId;
            if (performanceMap[productId]) {
              performanceMap[productId].redeemed += 1;
            }
          }
        });

      const products: ProductPerformance[] = Object.entries(performanceMap)
        .map(([name, data]) => ({
          name,
          issued: data.issued,
          redeemed: data.redeemed,
          redemptionRate: data.issued > 0 ? (data.redeemed / data.issued) * 100 : 0,
          totalValue: data.totalValue,
        }))
        .sort((a, b) => b.totalValue - a.totalValue);

      setProductPerformance(products);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load analytics");
      console.error("Error loading analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!analytics) return;

    setExportingFormat("csv");
    const csv = [
      ["Club Analytics Report"],
      [`Period: ${timeRange === "custom" ? `${customStart} to ${customEnd}` : timeRange}`],
      [],
      ["METRICS"],
      ["Total Users", analytics.totalUsers],
      ["Active Users", analytics.activeUsers],
      ["Total Issued", analytics.totalIssued],
      ["Total Redeemed", analytics.totalRedeemed],
      ["Redemption Rate %", analytics.redemptionRate],
      [],
      ["TOP PRODUCTS"],
      ["Rank", "Product", "Count"],
      ...analytics.topProducts.map((p, i) => [i + 1, p.name, p.count]),
      [],
      ["RECENT TRANSACTIONS"],
      ["Type", "User", "Amount", "Status", "Timestamp"],
      ...analytics.recentTransactions.map((tx) => [
        tx.type,
        tx.userName,
        tx.amount,
        tx.status,
        tx.timestamp.toLocaleString("no-NO"),
      ]),
      [],
      ["PRODUCT PERFORMANCE"],
      ["Product", "Issued", "Redeemed", "Redemption Rate %", "Total Value"],
      ...productPerformance.map((p) => [p.name, p.issued, p.redeemed, p.redemptionRate.toFixed(1), p.totalValue]),
      [],
      ["USER BEHAVIOR (Top 10)"],
      ["Name", "Email", "Transactions", "Total Spent", "Avg Transaction", "Last Activity"],
      ...userBehaviors.slice(0, 10).map((u) => [
        u.displayName,
        u.email,
        u.transactionCount,
        u.totalSpent,
        u.averageTransaction.toFixed(2),
        u.lastActivity.toLocaleString("no-NO"),
      ]),
    ];

    const csvContent = csv.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setExportingFormat(null);
  };

  const scheduleEmailReport = async () => {
    if (!reportEmail) {
      alert("Please enter an email address");
      return;
    }

    try {
      const scheduleReport = httpsCallable(functions, "schedule_report");
      await scheduleReport({
        email: reportEmail,
        schedule: reportSchedule,
        includeProducts: true,
        includeBehavior: true,
      });

      alert("Report scheduled successfully!");
      setShowReportDialog(false);
      setReportEmail("");
    } catch (err) {
      console.error("Error scheduling report:", err);
      alert("Failed to schedule report");
    }
  };

  if (loading) {
    return (
      <div className="club-analytics-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="club-analytics-page">
        <div className="error-container">
          <h3>⚠️ Error Loading Analytics</h3>
          <p>{error || "Failed to load analytics"}</p>
          <button onClick={loadAnalytics} className="btn-retry">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="club-analytics-page">
      <div className="page-header">
        <h1>📊 Advanced Club Analytics</h1>
        <p className="page-subtitle">Comprehensive analytics with user behavior and product performance</p>
      </div>

      {/* Advanced Date Range Selector */}
      <div className="date-range-section">
        <div className="preset-buttons">
          <button className={`time-btn ${timeRange === "7d" ? "active" : ""}`} onClick={() => setTimeRange("7d")}>
            7 Days
          </button>
          <button className={`time-btn ${timeRange === "30d" ? "active" : ""}`} onClick={() => setTimeRange("30d")}>
            30 Days
          </button>
          <button className={`time-btn ${timeRange === "90d" ? "active" : ""}`} onClick={() => setTimeRange("90d")}>
            90 Days
          </button>
          <button className={`time-btn ${timeRange === "custom" ? "active" : ""}`} onClick={() => setTimeRange("custom")}>
            Custom Range
          </button>
        </div>

        {timeRange === "custom" && (
          <div className="custom-range-inputs">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              placeholder="Start Date"
              className="form-input"
            />
            <span className="date-separator">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              placeholder="End Date"
              className="form-input"
            />
          </div>
        )}
      </div>

      {/* Export & Actions */}
      <div className="actions-bar">
        <button onClick={exportToCSV} disabled={exportingFormat === "csv"} className="btn-action">
          📥 {exportingFormat === "csv" ? "Exporting..." : "Export CSV"}
        </button>
        <button onClick={() => setShowReportDialog(true)} className="btn-action">
          📧 Schedule Email Report
        </button>
      </div>

      {/* Email Report Dialog */}
      {showReportDialog && (
        <div className="modal-overlay" onClick={() => setShowReportDialog(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>📧 Schedule Email Report</h3>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
                placeholder="your@email.com"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Frequency</label>
              <select
                value={reportSchedule}
                onChange={(e) => setReportSchedule(e.target.value as any)}
                className="form-select"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowReportDialog(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={scheduleEmailReport} className="btn-confirm">
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">👥 Total Users</div>
          <div className="metric-value">{analytics.totalUsers}</div>
          <div className="metric-subtext">{analytics.activeUsers} active</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">💰 Total Issued</div>
          <div className="metric-value">{analytics.totalIssued.toLocaleString("no-NO")}</div>
          <div className="metric-subtext">NOK in giftcards</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">✅ Total Redeemed</div>
          <div className="metric-value">{analytics.totalRedeemed.toLocaleString("no-NO")}</div>
          <div className="metric-subtext">{analytics.redemptionRate}% rate</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">⏳ Unredeemed</div>
          <div className="metric-value">{analytics.totalUnredeemed.toLocaleString("no-NO")}</div>
          <div className="metric-subtext">still available</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-card">
          <h3 className="chart-title">📈 Redemption Rate</h3>
          <div className="redemption-chart">
            <div
              className="chart-circle"
              style={{
                "--percentage": `${analytics.redemptionRate}%`,
              } as any}
            >
              <div className="chart-value">
                {analytics.redemptionRate.toFixed(1)}<span className="chart-unit">%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">🏆 Top Products</h3>
          <div className="bar-chart">
            {analytics.topProducts.length > 0 ? (
              analytics.topProducts.map((product, idx) => (
                <div key={idx} className="product-item">
                  <span className="product-rank">{idx + 1}</span>
                  <div className="product-bar">
                    <div
                      className="product-bar-fill"
                      style={{
                        width: `${(product.count / analytics.topProducts[0].count) * 100}%`,
                      }}
                    >
                      <span className="product-name">{product.name}</span>
                    </div>
                  </div>
                  <span className="product-count">{product.count}</span>
                </div>
              ))
            ) : (
              <p style={{ color: "#999" }}>No product data</p>
            )}
          </div>
        </div>
      </div>

      {/* Product Performance Section */}
      <div className="performance-section">
        <h3>📊 Product Performance Analysis</h3>
        <div className="performance-table-wrapper">
          {productPerformance.length > 0 ? (
            <table className="performance-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Issued</th>
                  <th>Redeemed</th>
                  <th>Redemption %</th>
                  <th>Total Value (NOK)</th>
                </tr>
              </thead>
              <tbody>
                {productPerformance.map((product, idx) => (
                  <tr key={idx}>
                    <td className="product-name-cell">{product.name}</td>
                    <td>{product.issued}</td>
                    <td>{product.redeemed}</td>
                    <td>
                      <span className="rate-badge">{product.redemptionRate.toFixed(1)}%</span>
                    </td>
                    <td className="value-cell">{product.totalValue.toLocaleString("no-NO")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ padding: "20px", color: "#999" }}>No product data available</p>
          )}
        </div>
      </div>

      {/* User Behavior Section */}
      <div className="behavior-section">
        <h3>👤 Top Users by Spending</h3>
        <div className="behavior-table-wrapper">
          {userBehaviors.length > 0 ? (
            <table className="behavior-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Transactions</th>
                  <th>Total Spent (NOK)</th>
                  <th>Avg per Transaction</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {userBehaviors.slice(0, 20).map((user, idx) => (
                  <tr key={idx}>
                    <td className="user-name-cell">{user.displayName}</td>
                    <td className="user-email-cell">{user.email}</td>
                    <td className="transaction-cell">{user.transactionCount}</td>
                    <td className="amount-cell">{user.totalSpent.toLocaleString("no-NO")}</td>
                    <td className="avg-cell">{user.averageTransaction.toFixed(0)}</td>
                    <td className="date-cell">{user.lastActivity.toLocaleDateString("no-NO")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ padding: "20px", color: "#999" }}>No user data available</p>
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="transactions-section">
        <h3 className="transactions-header">📋 Recent Transactions</h3>
        {analytics.recentTransactions.length > 0 ? (
          <div className="transactions-table-wrapper">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recentTransactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      <span className="transaction-type">{tx.type}</span>
                    </td>
                    <td className="transaction-user">{tx.userName}</td>
                    <td className="transaction-amount">{tx.amount} NOK</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor:
                            tx.status === "COMPLETED" ? "#2ecc71" : tx.status === "PENDING" ? "#f39c12" : "#e74c3c",
                        }}
                      >
                        {tx.status}
                      </span>
                    </td>
                    <td className="transaction-timestamp">{tx.timestamp.toLocaleString("no-NO")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ padding: "20px", color: "#999", textAlign: "center" }}>No transactions in this period</p>
        )}
      </div>
    </div>
  );
}
